// A from/to pair of calendar days (YYYY-MM-DD, as <input type="date"> gives
// them) picked in the user's own time zone. Either end may be open.
export interface DayRange {
  from?: string;
  to?: string;
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function localMidnight(day: string, offsetDays = 0): Date | undefined {
  const m = DAY.exec(day);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + offsetDays);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

// The instant the local day starts, as an ISO timestamp.
export function localDayStart(day: string): string | undefined {
  return localMidnight(day)?.toISOString();
}

// The last millisecond of the local day, as an ISO timestamp. Bounds are
// inclusive on the server, so "to 3 Oct" covers all of 3 Oct.
export function localDayEnd(day: string): string | undefined {
  const next = localMidnight(day, 1);
  return next && new Date(next.getTime() - 1).toISOString();
}

// Sets `${name}From` / `${name}To` to the instants bounding the picked
// days. The server reads them as inclusive RFC3339 bounds.
export function setDayRangeParams(qs: URLSearchParams, name: string, range?: DayRange) {
  const from = range?.from && localDayStart(range.from);
  const to = range?.to && localDayEnd(range.to);
  if (from) qs.set(`${name}From`, from);
  if (to) qs.set(`${name}To`, to);
}

export function isDayRangeSet(range?: DayRange): boolean {
  return !!(range?.from || range?.to);
}

// Whether a stored timestamp falls inside the picked local days. A missing
// or unparseable timestamp never matches a set range (as on the server).
export function inDayRange(value: unknown, range?: DayRange): boolean {
  if (!isDayRangeSet(range)) return true;
  if (typeof value !== "string" || value === "") return false;
  const at = Date.parse(value);
  if (Number.isNaN(at)) return false;
  const from = range?.from ? localDayStart(range.from) : undefined;
  const to = range?.to ? localDayEnd(range.to) : undefined;
  return (!from || at >= Date.parse(from)) && (!to || at <= Date.parse(to));
}
